import React from 'react';
import styled from 'styled-components';

const MathContainer = styled.div`
  display: inline-block;
  font-family: 'Times New Roman', serif;
  font-size: 1.2em;
  line-height: 1.4;
  vertical-align: middle;
`;

const Fraction = styled.div`
  display: inline-block;
  vertical-align: middle;
  text-align: center;
  margin: 0 0.2em;
`;

const Numerator = styled.div`
  border-bottom: 1px solid #000;
  padding: 0 0.3em 0.1em 0.3em;
  font-size: 0.9em;
`;

const Denominator = styled.div`
  padding: 0.1em 0.3em 0 0.3em;
  font-size: 0.9em;
`;

const Parentheses = styled.span`
  font-size: 1.3em;
  vertical-align: middle;
  margin: 0 0.1em;
`;

const Operator = styled.span`
  margin: 0 0.3em;
  font-weight: bold;
`;

const SquareRoot = styled.span`
  position: relative;
  display: inline-block;
  margin: 0 0.2em;
  
  &::before {
    content: '√';
    font-size: 1.2em;
    vertical-align: middle;
  }
`;

const Radical = styled.span`
  border-top: 1px solid #000;
  padding: 0 0.2em;
  margin-left: 0.1em;
`;

interface MathRendererProps {
  latex: string;
  className?: string;
}

const MathRenderer: React.FC<MathRendererProps> = ({ latex, className }) => {
  // Simple LaTeX parser for basic mathematical expressions
  const renderMath = (expression: string): JSX.Element => {
    // Handle the specific expression: \frac{2}{3} - \left(\frac{3}{4} - \frac{1}{2}\right) = 
    if (expression.includes('\\frac{2}{3} - \\left(\\frac{3}{4} - \\frac{1}{2}\\right)')) {
      return (
        <MathContainer className={className}>
          <Fraction>
            <Numerator>2</Numerator>
            <Denominator>3</Denominator>
          </Fraction>
          <Operator>−</Operator>
          <Parentheses>(</Parentheses>
          <Fraction>
            <Numerator>3</Numerator>
            <Denominator>4</Denominator>
          </Fraction>
          <Operator>−</Operator>
          <Fraction>
            <Numerator>1</Numerator>
            <Denominator>2</Denominator>
          </Fraction>
          <Parentheses>)</Parentheses>
          <Operator>=</Operator>
        </MathContainer>
      );
    }

    // Handle the new specific expression: \frac{5}{3} - \left( \frac{7}{2} - \frac{7}{5}\right) =
    if (expression.includes('\\frac{5}{3} - \\left( \\frac{7}{2} - \\frac{7}{5}\\right)')) {
      return (
        <MathContainer className={className}>
          <Fraction>
            <Numerator>5</Numerator>
            <Denominator>3</Denominator>
          </Fraction>
          <Operator>−</Operator>
          <Parentheses>(</Parentheses>
          <Fraction>
            <Numerator>7</Numerator>
            <Denominator>2</Denominator>
          </Fraction>
          <Operator>−</Operator>
          <Fraction>
            <Numerator>7</Numerator>
            <Denominator>5</Denominator>
          </Fraction>
          <Parentheses>)</Parentheses>
          <Operator>=</Operator>
        </MathContainer>
      );
    }

    // Handle negative fractions like -\frac{2}{3}
    if (expression.match(/^-\s*\\frac\{[^}]+\}\{[^}]+\}$/)) {
      const fractionMatch = expression.match(/-\s*\\frac\{([^}]+)\}\{([^}]+)\}/);
      if (fractionMatch) {
        return (
          <MathContainer className={className}>
            <Operator>−</Operator>
            <Fraction>
              <Numerator>{fractionMatch[1]}</Numerator>
              <Denominator>{fractionMatch[2]}</Denominator>
            </Fraction>
          </MathContainer>
        );
      }
    }

    // General fraction parser
    const fractionRegex = /\\frac\{([^}]+)\}\{([^}]+)\}/g;
    const negativeFractionRegex = /-\s*\\frac\{([^}]+)\}\{([^}]+)\}/g;
    const sqrtRegex = /\\sqrt\{([^}]+)\}/g;
    let result = expression;
    const fractions: JSX.Element[] = [];
    const sqrts: JSX.Element[] = [];
    let match;
    let fractionIndex = 0;
    let sqrtIndex = 0;

    // Handle negative fractions first
    while ((match = negativeFractionRegex.exec(expression)) !== null) {
      const numerator = match[1];
      const denominator = match[2];
      fractions.push(
        <span key={fractionIndex}>
          <Operator>−</Operator>
          <Fraction>
            <Numerator>{numerator}</Numerator>
            <Denominator>{denominator}</Denominator>
          </Fraction>
        </span>
      );
      result = result.replace(match[0], `__FRACTION_${fractionIndex}__`);
      fractionIndex++;
    }

    // Reset regex for regular fractions
    fractionRegex.lastIndex = 0;

    // Handle regular fractions
    while ((match = fractionRegex.exec(expression)) !== null) {
      const numerator = match[1];
      const denominator = match[2];
      fractions.push(
        <Fraction key={fractionIndex}>
          <Numerator>{numerator}</Numerator>
          <Denominator>{denominator}</Denominator>
        </Fraction>
      );
      result = result.replace(match[0], `__FRACTION_${fractionIndex}__`);
      fractionIndex++;
    }

    // Reset regex for square roots
    sqrtRegex.lastIndex = 0;
    
    // Handle square roots
    while ((match = sqrtRegex.exec(expression)) !== null) {
      const radicand = match[1];
      sqrts.push(
        <SquareRoot key={sqrtIndex}>
          <Radical>{radicand}</Radical>
        </SquareRoot>
      );
      result = result.replace(match[0], `__SQRT_${sqrtIndex}__`);
      sqrtIndex++;
    }

    // Replace common LaTeX symbols
    result = result
      .replace(/\\left\(/g, '(')
      .replace(/\\right\)/g, ')')
      .replace(/\\left\[/g, '[')
      .replace(/\\right\]/g, ']')
      .replace(/\\cdot/g, '·')
      .replace(/\\times/g, '×')
      .replace(/\\div/g, '÷')
      .replace(/\\pm/g, '±')
      .replace(/\\mp/g, '∓')
      .replace(/\\leq/g, '≤')
      .replace(/\\geq/g, '≥')
      .replace(/\\neq/g, '≠')
      .replace(/\\approx/g, '≈')
      .replace(/\\infty/g, '∞')
      .replace(/\\pi/g, 'π')
      .replace(/\\alpha/g, 'α')
      .replace(/\\beta/g, 'β')
      .replace(/\\gamma/g, 'γ')
      .replace(/\\delta/g, 'δ')
      .replace(/\\theta/g, 'θ')
      .replace(/\\lambda/g, 'λ')
      .replace(/\\mu/g, 'μ')
      .replace(/\\sigma/g, 'σ')
      .replace(/\\phi/g, 'φ')
      .replace(/\\omega/g, 'ω');

    // Split by fraction and sqrt placeholders and render
    const parts = result.split(/(__FRACTION_\d+__|__SQRT_\d+__)/);
    
    return (
      <MathContainer className={className}>
        {parts.map((part, i) => {
          const fractionMatch = part.match(/__FRACTION_(\d+)__/);
          const sqrtMatch = part.match(/__SQRT_(\d+)__/);
          
          if (fractionMatch) {
            const index = parseInt(fractionMatch[1]);
            return fractions[index];
          }
          
          if (sqrtMatch) {
            const index = parseInt(sqrtMatch[1]);
            return sqrts[index];
          }
          
          return <span key={i}>{part}</span>;
        })}
      </MathContainer>
    );
  };

  // Remove LaTeX delimiters if present
  const cleanLatex = latex
    .replace(/^\\\(/, '')
    .replace(/\\\)$/, '')
    .replace(/^\$/, '')
    .replace(/\$$/, '');

  return renderMath(cleanLatex);
};

export default MathRenderer;
